const ZONES_ORDER = ["WALL", "FLOOR", "TABLE"];

/**
 * Минимальный контракт для сортировки: достаточно опционального `title`.
 */
export type ZoneWithTitleForSort = {
  title?: string | null;
};

/**
 * Возвращает обрезанный по краям `title` или пустую строку.
 *
 * @param item - объект с полем `title`
 * @returns строка для сравнения и разбора префикса подзоны
 */
function getTitle(item: ZoneWithTitleForSort): string {
  return (item.title ?? "").trim();
}

/**
 * Числовой приоритет группы: индекс префикса в списке WALL → FLOOR → TABLE, иначе значение `length` (прочие).
 *
 * @param item - зона с `title`
 * @returns порядок от `0` (WALL) до `ZONES_ORDER.length` (прочие)
 */
function orderOfByTitle(item: ZoneWithTitleForSort): number {
  const title_upper = getTitle(item).toUpperCase();
  const order_idx = ZONES_ORDER.findIndex((z) => title_upper.startsWith(z));

  return order_idx === -1 ? ZONES_ORDER.length : order_idx;
}

/**
 * Строковый ключ для лексикографического сравнения: сначала приоритет группы (двузначный), затем `title`.
 *
 * @param item - зона с `title`
 * @returns ключ вида `«00_title»` … `«03_title»`
 */
function sortKey(item: ZoneWithTitleForSort): string {
  return `${orderOfByTitle(item).toString().padStart(2, "0")}_${getTitle(item)}`;
}

/**
 * Сортирует зоны по подзонам: префиксы title `WALL`, `FLOOR`, `TABLE` (без учёта регистра), остальные — после них;
 * внутри одной группы — по алфавиту `title` (`localeCompare` с числовым режимом).
 * Исходный массив не изменяется.
 *
 * @template T - тип элемента, расширяющий {@link ZoneWithTitleForSort}
 * @param data - массив зон
 * @returns новый отсортированный массив
 */
export function sortZonesByTitle<T extends ZoneWithTitleForSort>(data: T[]): T[] {
  return [...data].sort((a, b) => sortKey(a).localeCompare(sortKey(b), undefined, { numeric: true }));
}
